import { userAxios } from '../index'
import { toProjectDateTime } from '../weightloss-inventory/dateFormat'

function apiErrorMessage(error, fallback) {
  const body = error?.apiResponse || error?.response?.data
  if (body?.message) return body.message
  const first = body?.errors && Object.values(body.errors).flat()[0]
  if (first) return first
  return error?.message || fallback
}

function unwrap(res) {
  const body = res?.data ?? res
  const status = String(body?.status || '').toLowerCase()
  if (status === 'error') {
    const err = new Error(body.message || 'IV API error')
    err.apiResponse = body
    throw err
  }
  return body
}

function extractList(body) {
  const payload = body?.data ?? body
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.data?.data)) return payload.data.data
  return []
}

function extractPaginated(body) {
  const payload = body?.data ?? body
  const meta = payload?.meta || payload
  const page = payload?.current_page ?? meta?.current_page
  const rows = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload)
      ? payload
      : extractList(body)
  const total = payload?.total ?? meta?.total ?? rows.length
  const from = payload?.from ?? meta?.from
  const to = payload?.to ?? meta?.to
  return {
    items: rows,
    total: Number(total) || 0,
    current_page: page != null ? Number(page) : 1,
    from: from != null ? Number(from) : (rows.length ? 1 : 0),
    to: to != null ? Number(to) : rows.length,
  }
}

function paginateResult(result, perPage, page) {
  const items = result.items || []
  const size = Number(perPage) || items.length || 10
  const requestedPage = Number(page) || 1
  if (items.length > size) {
    const total = result.total && result.total >= items.length ? result.total : items.length
    const start = (requestedPage - 1) * size
    return {
      items: items.slice(start, start + size),
      total,
      current_page: requestedPage,
      from: total ? start + 1 : 0,
      to: Math.min(start + size, total),
    }
  }
  const total = Number(result.total) || items.length
  const start = (requestedPage - 1) * size
  return {
    items,
    total,
    current_page: result.current_page || requestedPage,
    from: result.from || (items.length ? start + 1 : 0),
    to: result.to || (items.length ? start + items.length : 0),
  }
}

const listAddons = async (perPage = 10, page = 1) => {
  // Test API registra GET api/iv/addons (query), no api/iv/addons/{paginate}.
  const res = await userAxios.get('iv/addons', {
    params: { page, per_page: perPage, paginate: perPage },
  })
  return paginateResult(extractPaginated(unwrap(res)), perPage, page)
}

const getAddon = async id => {
  const res = await userAxios.get(`iv/addons/detail/${id}`)
  const body = unwrap(res)
  return body?.data ?? body
}

const createAddon = async data => {
  const res = await userAxios.post('iv/addons', data)
  return unwrap(res)
}

const updateAddon = async (id, data) => {
  const res = await userAxios.put(`iv/addons/detail/${id}`, data)
  return unwrap(res)
}

const deleteAddon = async id => {
  const res = await userAxios.delete(`iv/addons/detail/${id}`)
  return unwrap(res)
}

const listPrices = async (perPage = 10, page = 1) => {
  const res = await userAxios.get('iv/prices', {
    params: { page, per_page: perPage, paginate: perPage },
  })
  return paginateResult(extractPaginated(unwrap(res)), perPage, page)
}

const listAllPrices = async () => {
  const pageSize = 200
  const first = await listPrices(pageSize, 1)
  const items = [...(first.items || [])]
  const total = Number(first.total) || items.length
  if (items.length >= total) return items
  const actualSize = items.length || pageSize
  const lastPage = Math.max(1, Math.ceil(total / actualSize))
  for (let page = 2; page <= lastPage; page++) {
    const next = await listPrices(pageSize, page)
    items.push(...(next.items || []))
  }
  return items
}

const createPrice = async data => {
  try {
    const res = await userAxios.post('iv/prices', data)
    return unwrap(res)
  } catch (error) {
    const err = new Error(apiErrorMessage(error, 'Could not save price'))
    err.status = error?.response?.status
    err.apiResponse = error?.response?.data || error?.apiResponse
    throw err
  }
}

const updatePrice = async (id, data) => {
  try {
    const res = await userAxios.put(`iv/prices/detail/${id}`, data)
    return unwrap(res)
  } catch (error) {
    const err = new Error(apiErrorMessage(error, 'Could not update price'))
    err.status = error?.response?.status
    err.apiResponse = error?.response?.data || error?.apiResponse
    throw err
  }
}

/**
 * POST api/iv/pricing/v2/quote — cotiza varios sueros con sus vitaminas (ambos carriles).
 * Emite `pricing`: `serums[]` en el orden del request, `serums_total`, `addons_total`, `iv_total`.
 */
const quoteSerums = async ({ facilityId, date, serums }) => {
  const payload = {
    date: date || toProjectDateTime(new Date()),
    serums,
  }
  if (facilityId != null && facilityId !== '') payload.facility_id = Number(facilityId)
  try {
    const res = await userAxios.post('iv/pricing/v2/quote', payload)
    const body = unwrap(res)
    return body?.data?.pricing ?? body?.pricing ?? null
  } catch (error) {
    const err = new Error(apiErrorMessage(error, 'Could not quote IV serums'))
    err.apiResponse = error?.response?.data || error?.apiResponse
    throw err
  }
}

export default {
  listAddons,
  getAddon,
  createAddon,
  updateAddon,
  deleteAddon,
  listPrices,
  listAllPrices,
  createPrice,
  updatePrice,
  quoteSerums,
}
